import {
  type TboAuditOperation,
  type TboAuditOutcome,
  type TboAuditProvider,
  type TboAuditRow,
  TboAuditOperationSchema,
  TboAuditOutcomeSchema,
  TboAuditProviderSchema,
} from '@utazzo/shared';
import { UzInput, UzDateRangePicker, type UzDateRange } from '@utazzo/ui';
import React, { useMemo, useState } from 'react';

import { TboAuditDetailDrawer } from '../components/TboAuditDetailDrawer';
import { formatDateTimeDMY, OutcomeBadge, shortId } from '../tbo-audit.helpers';
import { useTboAuditList } from '../use-tbo-audit';

import { DataGrid, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useDataGridState } from '@/hooks/use-data-grid-state';

/**
 * Admin-only viewer over the TBO API audit log. Every TBO flight/hotel call
 * (search, fare rule/quote, pre-book, book, hotel search/room/book, auth) is
 * recorded by the non-blocking recorder; this grid pages, filters and drills
 * into each one. Read-only.
 */

const humanize = (v: string): string =>
  v
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const PROVIDER_OPTIONS = [
  { value: '', label: 'All providers' },
  ...TboAuditProviderSchema.options.map((p) => ({ value: p, label: humanize(p) })),
] as const;

const OPERATION_OPTIONS = [
  { value: '', label: 'All operations' },
  ...TboAuditOperationSchema.options.map((o) => ({ value: o, label: humanize(o) })),
] as const;

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  ...TboAuditOutcomeSchema.options.map((o) => ({ value: o, label: humanize(o) })),
] as const;

const STATIC_FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'provider',
    label: 'Provider',
    options: PROVIDER_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'operation',
    label: 'Operation',
    options: OPERATION_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
  {
    id: 'outcome',
    label: 'Outcome',
    options: OUTCOME_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

const FILTER_IDS = ['provider', 'operation', 'outcome'] as const;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Only forward an id filter when it looks like a real uuid (avoids 400s). */
const asUuid = (raw: string): string | undefined => {
  const v = raw.trim();
  return UUID_RE.test(v) ? v : undefined;
};

const asEnum = <T extends string>(
  value: string | undefined,
  allowed: ReadonlyArray<T>,
): T | undefined =>
  value && (allowed as ReadonlyArray<string>).includes(value) ? (value as T) : undefined;

const startOfDayIso = (d: Date): string => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
};
const endOfDayIso = (d: Date): string => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
};

export const TboAuditListPage = (): React.JSX.Element => {
  const gridState = useDataGridState({
    filterIds: FILTER_IDS as unknown as string[],
    defaultSortBy: 'createdAt',
    defaultSortDir: 'desc',
  });
  const { state } = gridState;

  const [range, setRange] = useState<UzDateRange>({ from: null, to: null });
  const [userId, setUserId] = useState('');
  const [leadId, setLeadId] = useState('');
  const [itineraryId, setItineraryId] = useState('');

  const [selected, setSelected] = useState<TboAuditRow | null>(null);

  const listQuery = useTboAuditList({
    page: state.page,
    pageSize: state.pageSize,
    sortBy: 'createdAt',
    sortDir: state.sortDir,
    provider: asEnum<TboAuditProvider>(state.filters.provider, TboAuditProviderSchema.options),
    operation: asEnum<TboAuditOperation>(state.filters.operation, TboAuditOperationSchema.options),
    outcome: asEnum<TboAuditOutcome>(state.filters.outcome, TboAuditOutcomeSchema.options),
    userId: asUuid(userId),
    leadId: asUuid(leadId),
    itineraryId: asUuid(itineraryId),
    from: range.from ? startOfDayIso(range.from) : undefined,
    to: range.to ? endOfDayIso(range.to) : undefined,
  });

  const rows = listQuery.data?.data ?? [];
  const pagination = listQuery.data
    ? {
        page: listQuery.data.pagination.page,
        pageSize: listQuery.data.pagination.pageSize,
        totalItems: listQuery.data.pagination.totalItems,
      }
    : null;

  const columns = useMemo((): ReadonlyArray<DataGridColumn<TboAuditRow>> => {
    return [
      {
        id: 'createdAt',
        header: 'Time',
        width: '160px',
        sortable: true,
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">
            {formatDateTimeDMY(row.createdAt)}
          </span>
        ),
      },
      {
        id: 'provider',
        header: 'Provider',
        width: '120px',
        cell: (row) => <span className="text-sm text-zinc-700">{humanize(row.provider)}</span>,
      },
      {
        id: 'operation',
        header: 'Operation',
        width: '130px',
        cell: (row) => <span className="text-sm text-zinc-700">{humanize(row.operation)}</span>,
      },
      {
        id: 'outcome',
        header: 'Outcome',
        width: '120px',
        cell: (row) => <OutcomeBadge outcome={row.outcome} />,
      },
      {
        id: 'source',
        header: 'Source',
        width: '120px',
        cell: (row) => <span className="text-sm text-zinc-600">{humanize(row.source)}</span>,
      },
      {
        id: 'latencyMs',
        header: 'Latency',
        width: '90px',
        align: 'right',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">
            {row.latencyMs != null ? `${row.latencyMs} ms` : '-'}
          </span>
        ),
      },
      {
        id: 'resultCount',
        header: 'Results',
        width: '80px',
        align: 'right',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">{row.resultCount ?? '-'}</span>
        ),
      },
      {
        id: 'httpStatus',
        header: 'HTTP',
        width: '70px',
        align: 'right',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">{row.httpStatus ?? '-'}</span>
        ),
      },
      {
        id: 'userId',
        header: 'User',
        width: '140px',
        cell: (row) => (
          <span className="text-sm text-zinc-600" title={row.userId ?? undefined}>
            {row.userName ?? shortId(row.userId)}
          </span>
        ),
      },
      {
        id: 'hasBody',
        header: 'Body',
        width: '70px',
        align: 'center',
        cell: (row) =>
          row.hasBody ? (
            <span className="text-xs font-medium text-emerald-600">Yes</span>
          ) : (
            <span className="text-xs text-zinc-400">-</span>
          ),
      },
    ];
  }, []);

  return (
    <PageShell
      title="TBO API Audit"
      description="Every TBO flight and hotel API call (search, fare quote, pre-book, book, and more) is logged here for debugging and audit. Read-only, admin-only."
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <UzDateRangePicker
          value={range}
          onValueChange={setRange}
          placeholder="Filter by date range"
          triggerClassName="w-64"
        />
        <UzInput
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="User id (uuid)"
          className="w-52"
        />
        <UzInput
          value={leadId}
          onChange={(e) => setLeadId(e.target.value)}
          placeholder="Lead id (uuid)"
          className="w-52"
        />
        <UzInput
          value={itineraryId}
          onChange={(e) => setItineraryId(e.target.value)}
          placeholder="Itinerary id (uuid)"
          className="w-52"
        />
      </div>

      <DataGrid
        gridId="tbo-audit"
        data={rows}
        columns={columns}
        filters={STATIC_FILTERS}
        getRowId={(row) => row.id}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        pagination={pagination}
        state={state}
        onStateChange={gridState}
        onRowClick={(row) => setSelected(row)}
        emptyTitle="No TBO calls logged yet"
        emptyDescription="The audit log fills as flight/hotel searches run (requires TBO_AUDIT_ENABLED)."
        tableLayout="auto"
      />

      <TboAuditDetailDrawer
        row={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </PageShell>
  );
};
